import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { TableSortOption, MessageConst } from 'enums'
import messageUtil from 'utils/messageUtil'
import { Theme } from 'features/ui/types'
import { InsightData } from 'schemas/action/InsightDataSchema'
import { useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { useOverflowObserver } from 'hooks/useOverflowObserver'
import { useEmojiNameToDisplayMap } from 'hooks/useEmojiState'
import { useDisplayTheme } from 'hooks/useUiState'
import TableChart, { ColDef, ColDefs } from 'components/visualization/Table'
import { getTwEmoji } from 'utils/emojiUtil'
// @ts-ignore
import { sys } from '@netbasequid/theme'
// @ts-ignore
import { SimpleBar } from '@netbasequid/chart'
// @ts-ignore
import { Tooltip, QuestionMarkOutlined } from '@netbasequid/core'
import useIndexWidgetData from '../useIndexWidgetData'
import {
  getDisplayText,
  findMaxValue,
  getIndexValue,
  getPercentByIndex,
  getNumberFormatter,
  getBarColor,
  getDisplayIndex,
  getSortingConfigByTableSortOption,
  getCompareIds
} from '../utils/index'
import { IndexWidgetsType } from '../../types'

interface IndexInsight {
  name: InsightData['name']
  value: InsightData['value']
  index: InsightData['index']
}

const StyledNameContainer = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
  overflow-wrap: break-word;
`

const NameContainer = ({ children, mode }: { children: React.ReactNode; mode: Theme }) => {
  const { isOverflowY, targetRef } = useOverflowObserver()

  const handleRef = (
    setTooltipTriggerRef: React.Dispatch<React.SetStateAction<HTMLElement | null>>
  ) => (StyledNameContainerRef: HTMLDivElement) => {
    targetRef.current = StyledNameContainerRef
    setTooltipTriggerRef(StyledNameContainerRef)
  }

  return (
    <Tooltip enabled={isOverflowY} content={children} mode={mode} callbackRefCreator={handleRef}>
      <StyledNameContainer ref={targetRef as React.MutableRefObject<HTMLDivElement>}>
        {children}
      </StyledNameContainer>
    </Tooltip>
  )
}

const IndexTable = ({
  widgetId,
  onRowClick
}: {
  widgetId: number
  onRowClick?: (insightData: IndexInsight) => void
}) => {
  const theme = useDisplayTheme() as Theme
  const { t } = useTranslation()
  const widgetSetting = useDashboardWidgetSetting<IndexWidgetsType>(widgetId)
  const { viewOption, tableSortOption, countType } = widgetSetting
  const emojiNameToDisplayMap = useEmojiNameToDisplayMap()
  const { insights: rowData, maxIndex } = useIndexWidgetData(widgetId) as {
    insights: IndexInsight[]
    maxIndex: number
  }
  const maxValue = findMaxValue(rowData)
  const hasCompareIds = getCompareIds(widgetSetting)
  const shouldFallbackSort = !hasCompareIds

  const colDefs: ColDefs<IndexInsight> = [
    {
      field: 'name',
      headerText: messageUtil.viewOption(viewOption!),
      cellProps: {
        width: '33%',
        textAlign: 'left',
        displayTooltip: false
      },
      comparator: (rowA: IndexInsight, rowB: IndexInsight) =>
        getDisplayText(rowA.name!, viewOption!, emojiNameToDisplayMap) <
        getDisplayText(rowB.name!, viewOption!, emojiNameToDisplayMap)
          ? -1
          : 1,
      customCellRender: (cellValue: any) => (
        <NameContainer mode={theme}>
          {getTwEmoji(getDisplayText(cellValue!, viewOption!, emojiNameToDisplayMap)) ||
            getDisplayText(cellValue!, viewOption!, emojiNameToDisplayMap)}
        </NameContainer>
      ),
      sortable: true,
      defaultOrder: TableSortOption.DESCEND
    },
    {
      field: 'value',
      headerText: messageUtil.countType(countType!),
      cellProps: {
        width: '33%',
        textAlign: 'left'
      },
      comparator: (rowA: IndexInsight, rowB: IndexInsight) => (rowA.value! > rowB.value! ? 1 : -1),
      children: [
        {
          customCellRender: (cellValue: any) => <>{getNumberFormatter().format(cellValue!)}</>,
          cellProps: {
            colSidePaddings: { right: '8px' },
            textAlign: 'right',
            fitWidth: true
          }
        },
        {
          customCellRender: (cellValue: any) => (
            <SimpleBar
              progress={(cellValue! / maxValue) * 100}
              colorSetting={getBarColor(countType!)}
              mode={theme}
            />
          ),
          cellProps: {
            colSidePaddings: { left: '0px' },
            textAlign: 'left'
          }
        }
      ],
      sortable: true,
      defaultOrder: TableSortOption.DESCEND
    }
  ]

  const commonLastColDef = {
    field: 'index',
    headerText: `${t('widgetType.benchmark')} (${t('vsComparison')})`,
    headerProps: {
      icon: {
        render: <QuestionMarkOutlined fontSize={14} color={sys[theme].text.placeholder} />,
        tooltipContent: t('indexWidgetTooltip.indexHeader')
      }
    },
    cellProps: {
      width: '33%',
      textAlign: 'left'
    }
  }

  if (hasCompareIds) {
    colDefs.push({
      ...commonLastColDef,
      comparator: (rowA: IndexInsight, rowB: IndexInsight) =>
        getIndexValue(rowA.index!) > getIndexValue(rowB.index!) ? 1 : -1,
      children: [
        {
          customCellRender: (cellValue) => <>{getDisplayIndex(cellValue!)}</>,
          cellProps: {
            colSidePaddings: { right: '8px' },
            textAlign: 'right',
            fitWidth: true
          }
        },
        {
          customCellRender: (cellValue) => (
            <SimpleBar
              progress={getPercentByIndex(cellValue!, maxIndex)}
              colorSetting={getBarColor(countType!)}
              mode={theme}
            />
          ),
          cellProps: {
            colSidePaddings: { left: '0px' },
            textAlign: 'left'
          }
        }
      ],
      sortable: true
    } as ColDef<IndexInsight, any, 'index'>)
  } else {
    colDefs.push({
      ...commonLastColDef,
      children: [
        {
          customCellRender: () => <>{MessageConst.EM_DASH}</>,
          cellProps: {
            colSidePaddings: { right: '8px' },
            textAlign: 'right',
            fitWidth: true,
            displayTooltip: true,
            tooltipContent: t('indexWidgetTooltip.noComparisonTopic')
          }
        },
        {
          customCellRender: () => <SimpleBar progress={0} mode={theme} />,
          cellProps: {
            colSidePaddings: { left: '0px' },
            textAlign: 'left',
            displayTooltip: true,
            tooltipContent: t('indexWidgetTooltip.noComparisonTopic')
          }
        }
      ],
      sortable: false
    } as ColDef<IndexInsight, any, 'index'>)
  }

  return (
    <TableChart
      rowData={rowData}
      configs={{
        colSortingConfigs: [
          getSortingConfigByTableSortOption(tableSortOption!, shouldFallbackSort)
        ],
        keyField: 'name'
      }}
      colDefs={colDefs}
      isDisplayFoot={false}
      onRowClick={(tableItem) => {
        const insightData = rowData.find((item) => item.name === tableItem.name)
        onRowClick?.(insightData!)
      }}
    />
  )
}

export default IndexTable
